import { useEffect, useMemo, useState } from "react";
import { getAvailableParsingDays } from "../api";
import { buildAnalyticsGuestsPayload } from "../../../utils/guests";

export const AVAILABLE_PARSING_DATES_ERROR = "Не удалось загрузить доступные даты парсинга";

export type AvailableParsingDatesInfo = {
  dates: string[];
  dateSet: Set<string>;
  loading: boolean;
  error: string | null;
  groupId: number | null;
  minStay: number;
  guestsValue: string;
  datesKey: string;
};

const emptyAvailableParsingDates = (minStay: number, guestsValue: string): AvailableParsingDatesInfo => ({
  dates: [],
  dateSet: new Set<string>(),
  loading: false,
  error: null,
  groupId: null,
  minStay,
  guestsValue,
  datesKey: "",
});

function normalizeIsoDates(dates: string[]) {
  return Array.from(
    new Set(
      dates
        .map((date) => date.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? "")
        .filter(Boolean),
    ),
  ).sort((left, right) => left.localeCompare(right));
}

function getDateTime(value: string) {
  const time = new Date(`${value}T00:00:00`).getTime();
  return Number.isNaN(time) ? null : time;
}

export function findClosestAvailableDate(target: string | null | undefined, availableDates: string[]) {
  if (!availableDates.length) return "";
  if (target && availableDates.includes(target)) return target;

  const targetTime = target ? getDateTime(target) : null;
  if (targetTime === null) return availableDates[availableDates.length - 1];

  return availableDates.reduce((closest, date) => {
    const closestTime = getDateTime(closest);
    const dateTime = getDateTime(date);
    if (dateTime === null) return closest;
    if (closestTime === null) return date;

    const currentDistance = Math.abs(dateTime - targetTime);
    const closestDistance = Math.abs(closestTime - targetTime);
    if (currentDistance < closestDistance) return date;
    if (currentDistance === closestDistance && dateTime > closestTime) return date;
    return closest;
  }, availableDates[0]);
}

export function normalizeAvailableDate(value: string, fallback: string, availableDates: string[]) {
  if (!availableDates.length) return "";
  return findClosestAvailableDate(value || fallback, availableDates);
}

export function normalizeAvailableDateRange(
  from: string,
  to: string,
  fallbackFrom: string,
  fallbackTo: string,
  availableDates: string[],
) {
  if (!availableDates.length) return { from: "", to: "" };

  const availableSet = new Set(availableDates);
  const nextFrom = availableSet.has(from)
    ? from
    : findClosestAvailableDate(from || fallbackFrom || fallbackTo || to, availableDates);
  const datesAfterFrom = availableDates.filter((date) => date >= nextFrom);
  const nextTo = availableSet.has(to) && to >= nextFrom
    ? to
    : findClosestAvailableDate(to || fallbackTo || nextFrom, datesAfterFrom);

  return {
    from: nextFrom,
    to: nextTo || nextFrom,
  };
}

export function isAvailableParsingDatesInfoReady(
  info: AvailableParsingDatesInfo,
  groupId: number | null,
  minStay: number,
  guestsValue: string,
  enabled = true,
) {
  if (!groupId || !enabled) return true;
  return !info.loading && info.groupId === groupId && info.minStay === minStay && info.guestsValue === guestsValue;
}

export function useAvailableParsingDates(
  groupId: number | null,
  minStay = 1,
  guestsValue = "2",
  enabled = true,
) {
  const [info, setInfo] = useState<AvailableParsingDatesInfo>(() => emptyAvailableParsingDates(minStay, guestsValue));
  const guestsPayload = useMemo(() => buildAnalyticsGuestsPayload(guestsValue), [guestsValue]);

  useEffect(() => {
    if (!groupId || !enabled) {
      setInfo(emptyAvailableParsingDates(minStay, guestsValue));
      return;
    }

    let isMounted = true;

    setInfo({
      dates: [],
      dateSet: new Set<string>(),
      loading: true,
      error: null,
      groupId: null,
      minStay,
      guestsValue,
      datesKey: "",
    });

    getAvailableParsingDays({
      group_id: groupId,
      min_stay: minStay,
      guests: guestsPayload.guests ?? 2,
      ...(guestsPayload.guests_child?.length ? { guests_child: guestsPayload.guests_child } : {}),
    })
      .then((dates) => {
        if (!isMounted) return;
        const normalizedDates = normalizeIsoDates(dates);
        setInfo({
          dates: normalizedDates,
          dateSet: new Set(normalizedDates),
          loading: false,
          error: null,
          groupId,
          minStay,
          guestsValue,
          datesKey: normalizedDates.join("|"),
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setInfo({
          dates: [],
          dateSet: new Set<string>(),
          loading: false,
          error: AVAILABLE_PARSING_DATES_ERROR,
          groupId,
          minStay,
          guestsValue,
          datesKey: "",
        });
      });

    return () => {
      isMounted = false;
    };
  }, [enabled, groupId, guestsPayload.guests, guestsPayload.guests_child, guestsValue, minStay]);

  return info;
}
